import { useEffect, useMemo, useState } from 'react';
import { Loader, Plus, Save, Settings2, ShieldAlert, Trash2, TriangleAlert, Undo2 } from 'lucide-react';
import {
  AMBITOS_REGLA,
  DESCRIPCION_REGLA_POR_TIPO,
  NIVELES_ALERTA,
  NOMBRES_RUTEO_MAX_IDS,
  TIPOS_ALERTA,
  TITULO_POR_TIPO,
  type AmbitoRegla,
  type EscalonNivel,
  type NivelAlerta,
  type NombresRuteoRespuesta,
  type ReglaAlerta,
  type TipoAlerta,
  type TroncalDetalle,
} from '@tms/contratos';
import { api } from '../api';
import { AMBITO, ICONO_TIPO_ALERTA, NIVEL } from '../components/estados';
import { useSesion } from '../state/Sesion';
import { useToast } from '../state/Toast';
import { useCatalogos } from '../util/catalogos';
import { fmtFechaHora } from '../util/format';
import { useCarga } from '../util/hooks';
import { puedeEditarReglas, puedeVerReglas } from '../util/roles';

const UNIDAD_TEXTO = { min: 'min', m: 'm', 'km/h': 'km/h' } as const;

interface NombresRuteo {
  rutas: NombresRuteoRespuesta['rutas'];
  paradas: NombresRuteoRespuesta['paradas'];
  /** true cuando ya se consultaron los ids pedidos: un id ausente pasa a «no encontrada». */
  listo: boolean;
}

const SIN_NOMBRES: NombresRuteoRespuesta = { rutas: {}, paradas: {} };

/** Ids numéricos de las referencias de ámbito `ambito` de las reglas dadas, sin repetir. */
function idsDe(reglas: ReglaAlerta[], ambito: AmbitoRegla): number[] {
  const ids = new Set<number>();
  for (const r of reglas) if (r.ambito === ambito && r.ambitoRef !== null && /^\d+$/.test(r.ambitoRef)) ids.add(Number(r.ambitoRef));
  return [...ids];
}

/**
 * Nombres de las rutas y paradas de las reglas visibles, con GET /ruteo/nombres: una sola llamada
 * (partida en tandas si hay más de NOMBRES_RUTEO_MAX_IDS ids) en vez de recorrer todas las troncales.
 */
function useNombresRuteo(visibles: ReglaAlerta[]): NombresRuteo {
  const [nombres, setNombres] = useState<NombresRuteoRespuesta>(SIN_NOMBRES);
  const [pedidos, setPedidos] = useState<ReadonlySet<string>>(new Set());
  const rutaIds = useMemo(() => idsDe(visibles, 'RUTA'), [visibles]);
  const paradaIds = useMemo(() => idsDe(visibles, 'PARADA_RUTA'), [visibles]);
  const firma = `${[...rutaIds].sort((a, b) => a - b).join(',')}|${[...paradaIds].sort((a, b) => a - b).join(',')}`;

  useEffect(() => {
    const faltanRutas = rutaIds.filter((id) => !pedidos.has(`r${id}`));
    const faltanParadas = paradaIds.filter((id) => !pedidos.has(`p${id}`));
    if (faltanRutas.length + faltanParadas.length === 0) return;
    let vigente = true;
    void (async () => {
      try {
        const tandas: { rutas: number[]; paradas: number[] }[] = [];
        const todos = [...faltanRutas.map((id) => ({ t: 'r' as const, id })), ...faltanParadas.map((id) => ({ t: 'p' as const, id }))];
        for (let i = 0; i < todos.length; i += NOMBRES_RUTEO_MAX_IDS) {
          const parte = todos.slice(i, i + NOMBRES_RUTEO_MAX_IDS);
          tandas.push({ rutas: parte.filter((x) => x.t === 'r').map((x) => x.id), paradas: parte.filter((x) => x.t === 'p').map((x) => x.id) });
        }
        const respuestas = await Promise.all(tandas.map((t) => api.nombresRuteo(t.rutas, t.paradas)));
        if (!vigente) return;
        setNombres((n) => ({
          rutas: Object.assign({}, n.rutas, ...respuestas.map((r) => r.rutas)),
          paradas: Object.assign({}, n.paradas, ...respuestas.map((r) => r.paradas)),
        }));
        setPedidos((p) => new Set([...p, ...faltanRutas.map((id) => `r${id}`), ...faltanParadas.map((id) => `p${id}`)]));
      } catch (e) {
        // Sin nombres se muestran los ids: no bloquea la pantalla.
        console.warn('No se pudieron cargar los nombres de rutas y paradas de las reglas', e);
      }
    })();
    return () => {
      vigente = false;
    };
    // `firma` resume los ids visibles; `pedidos` solo se actualiza desde este efecto.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firma]);

  const listo = rutaIds.every((id) => pedidos.has(`r${id}`)) && paradaIds.every((id) => pedidos.has(`p${id}`));
  return { rutas: nombres.rutas, paradas: nombres.paradas, listo };
}

function etiquetaAmbito(r: { ambito: AmbitoRegla; ambitoRef: string | null }, n: NombresRuteo): string {
  if (r.ambito === 'GENERAL' || r.ambitoRef === null) return AMBITO[r.ambito];
  const id = r.ambitoRef;
  if (r.ambito === 'RUTA') {
    const ruta = n.rutas[id];
    if (ruta) return `${AMBITO.RUTA}: ${ruta.troncalNombre} · ${ruta.nombre}${ruta.activa ? '' : ' (dada de baja)'}`;
    return `${AMBITO.RUTA}: Ruta #${id}${n.listo ? ' (no encontrada)' : ''}`;
  }
  if (r.ambito === 'PARADA_RUTA') {
    const parada = n.paradas[id];
    if (parada) return `${AMBITO.PARADA_RUTA}: ${parada.rutaNombre} · ${parada.nombre}`;
    return `${AMBITO.PARADA_RUTA}: Parada #${id}${n.listo ? ' (no encontrada)' : ''}`;
  }
  return `${AMBITO[r.ambito]}: ${id}`;
}

/** Troncales con sus rutas y paradas, solo para el selector de «Nueva regla» (se carga al necesitarlo). */
function useTroncalesConRutas(activo: boolean): TroncalDetalle[] {
  const [detalles, setDetalles] = useState<TroncalDetalle[]>([]);
  const [cargado, setCargado] = useState(false);
  useEffect(() => {
    if (!activo || cargado) return;
    setCargado(true);
    void (async () => {
      try {
        const lista = await api.troncales({ tamanio: 200 });
        setDetalles(await Promise.all(lista.items.map((t) => api.troncal(t.id))));
      } catch (e) {
        console.warn('No se pudieron cargar las rutas para elegir el ámbito de la regla', e);
      }
    })();
  }, [activo, cargado]);
  return detalles;
}

interface Borrador {
  activa: boolean;
  parametros: Record<string, number>;
  niveles: EscalonNivel[];
}

const borradorDe = (r: Pick<ReglaAlerta, 'activa' | 'parametros' | 'niveles'>): Borrador => ({
  activa: r.activa,
  parametros: { ...r.parametros },
  niveles: r.niveles.map((n) => ({ ...n })),
});

/** Valida el borrador y devuelve el mensaje de error, o null si está bien. */
function validar(tipo: TipoAlerta, b: Borrador): string | null {
  const d = DESCRIPCION_REGLA_POR_TIPO[tipo];
  if (b.niveles.length === 0) return 'Hace falta al menos un nivel.';
  for (const p of d.parametros) {
    const v = b.parametros[p.clave];
    if (v === undefined || Number.isNaN(v) || v < 0) return `Completá «${p.descripcion.replace(/ \(.*\)$/, '')}» con un número positivo.`;
  }
  for (let i = 1; i < b.niveles.length; i++) if (b.niveles[i].desde <= b.niveles[i - 1].desde) return 'Los escalones tienen que ir en orden creciente, sin repetir el valor.';
  return null;
}

function EditorBorrador({ tipo, b, setB, deshabilitado }: { tipo: TipoAlerta; b: Borrador; setB: (b: Borrador) => void; deshabilitado: boolean }) {
  const d = DESCRIPCION_REGLA_POR_TIPO[tipo];
  const sinMagnitud = d.magnitud.unidad === null;
  const cambiarNivel = (i: number, cambios: Partial<EscalonNivel>) => setB({ ...b, niveles: b.niveles.map((n, j) => (j === i ? { ...n, ...cambios } : n)) });
  return (
    <>
      {d.parametros.length > 0 && (
        <div className="regla-params">
          {d.parametros.map((p) => (
            <label key={p.clave} className="field" title={p.descripcion}>
              <span>
                {p.descripcion.replace(/ \(.*\)$/, '')} ({UNIDAD_TEXTO[p.unidad]})
              </span>
              <input
                type="number"
                min={0}
                disabled={deshabilitado}
                value={Number.isNaN(b.parametros[p.clave]) ? '' : (b.parametros[p.clave] ?? '')}
                onChange={(e) => setB({ ...b, parametros: { ...b.parametros, [p.clave]: e.target.value === '' ? NaN : Number(e.target.value) } })}
              />
            </label>
          ))}
        </div>
      )}
      <div>
        <div className="ayuda" style={{ marginBottom: 6 }}>
          Niveles: {d.magnitud.descripcion}
          {sinMagnitud ? ' (un solo nivel)' : `. Cada escalón rige desde ese valor (${UNIDAD_TEXTO[d.magnitud.unidad!]}); el nivel puede subir mientras la alerta sigue abierta, nunca bajar.`}
        </div>
        <div className="niveles-lista">
          {b.niveles.map((n, i) => {
            const Icono = NIVEL[n.nivel].icono;
            return (
              <div key={i} className="nivel-fila">
                {!sinMagnitud && (
                  <>
                    <span>Desde</span>
                    <input type="number" min={0} disabled={deshabilitado} value={n.desde} aria-label={`Valor del escalón ${i + 1}`} onChange={(e) => cambiarNivel(i, { desde: Number(e.target.value) })} />
                    <span>{UNIDAD_TEXTO[d.magnitud.unidad!]} →</span>
                  </>
                )}
                <Icono aria-hidden size={14} className={NIVEL[n.nivel].color} />
                <select disabled={deshabilitado} value={n.nivel} aria-label={`Nivel del escalón ${i + 1}`} onChange={(e) => cambiarNivel(i, { nivel: e.target.value as NivelAlerta })}>
                  {NIVELES_ALERTA.map((x) => (
                    <option key={x} value={x}>
                      {NIVEL[x].etiqueta}
                    </option>
                  ))}
                </select>
                {!sinMagnitud && b.niveles.length > 1 && !deshabilitado && (
                  <button type="button" className="btn-sm" aria-label={`Quitar el escalón ${i + 1}`} onClick={() => setB({ ...b, niveles: b.niveles.filter((_, j) => j !== i) })}>
                    <Trash2 aria-hidden size={13} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
        {!sinMagnitud && !deshabilitado && (
          <button
            type="button"
            className="btn-sm"
            style={{ marginTop: 8 }}
            onClick={() => {
              const ult = b.niveles[b.niveles.length - 1];
              setB({ ...b, niveles: [...b.niveles, { desde: (ult?.desde ?? 0) + 10, nivel: 'CRITICA' }] });
            }}
          >
            <Plus aria-hidden size={13} />
            Agregar escalón
          </button>
        )}
      </div>
    </>
  );
}

function FilaRegla({ regla, nombres, puedeEditar, onCambio }: { regla: ReglaAlerta; nombres: NombresRuteo; puedeEditar: boolean; onCambio: () => void }) {
  const { avisar } = useToast();
  const [b, setB] = useState<Borrador>(() => borradorDe(regla));
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sucio = JSON.stringify(b) !== JSON.stringify(borradorDe(regla));

  const guardar = async () => {
    const e = validar(regla.tipo, b);
    if (e) return setError(e);
    setError(null);
    setGuardando(true);
    try {
      await api.actualizarReglaAlerta(regla.id, b);
      avisar('Regla guardada. Rige para las alertas nuevas y para las que se reevalúan.');
      onCambio();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo guardar la regla.');
    } finally {
      setGuardando(false);
    }
  };

  const borrar = async () => {
    if (!window.confirm(`¿Borrar la regla «${etiquetaAmbito(regla, nombres)}»? Las alertas de ese ámbito pasan a evaluarse con la regla más general que aplique.`)) return;
    try {
      await api.borrarReglaAlerta(regla.id);
      avisar('Regla borrada');
      onCambio();
    } catch (err) {
      avisar(err instanceof Error ? err.message : 'No se pudo borrar la regla.', 'error');
    }
  };

  return (
    <div className={`regla-item ${b.activa ? '' : 'inactiva'}`}>
      <div className="regla-cab">
        <strong className="grow">{etiquetaAmbito(regla, nombres)}</strong>
        <label className="interruptor" title="Una regla inactiva no genera alertas, y no cae a una regla más general">
          <input type="checkbox" checked={b.activa} disabled={!puedeEditar} onChange={(e) => setB({ ...b, activa: e.target.checked })} />
          {b.activa ? 'Activa' : 'Inactiva: no genera alertas'}
        </label>
      </div>
      <EditorBorrador tipo={regla.tipo} b={b} setB={setB} deshabilitado={!puedeEditar} />
      {error && (
        <div className="banner error" role="alert" style={{ marginBottom: 0 }}>
          <TriangleAlert aria-hidden />
          {error}
        </div>
      )}
      <div className="editor-acciones">
        {puedeEditar && (
          <>
            <button type="button" className="primary btn-sm" disabled={!sucio || guardando} onClick={() => void guardar()}>
              <Save aria-hidden size={13} />
              {guardando ? 'Guardando…' : 'Guardar'}
            </button>
            <button type="button" className="btn-sm" disabled={!sucio || guardando} onClick={() => { setB(borradorDe(regla)); setError(null); }}>
              <Undo2 aria-hidden size={13} />
              Descartar cambios
            </button>
            {regla.ambito !== 'GENERAL' && (
              <button type="button" className="btn-sm" onClick={() => void borrar()}>
                <Trash2 aria-hidden size={13} />
                Borrar regla
              </button>
            )}
          </>
        )}
        <span className="ayuda" style={{ marginLeft: 'auto' }}>
          {regla.actualizadoPor ? `Editada por ${regla.actualizadoPor} · ${fmtFechaHora(regla.actualizadoEn)}` : 'Valores iniciales (variables de entorno)'}
        </span>
      </div>
    </div>
  );
}

/** Alta de una regla más específica que la general, copiando sus valores como punto de partida. */
function NuevaRegla({ tipo, general, existentes, onCreada }: { tipo: TipoAlerta; general: ReglaAlerta | undefined; existentes: ReglaAlerta[]; onCreada: () => void }) {
  const { avisar } = useToast();
  const catalogos = useCatalogos();
  const [abierto, setAbierto] = useState(false);
  const [ambito, setAmbito] = useState<Exclude<AmbitoRegla, 'GENERAL'>>('EMPRESA');
  const [ref, setRef] = useState('');
  const [troncalSel, setTroncalSel] = useState('');
  const [rutaSel, setRutaSel] = useState('');
  const [b, setB] = useState<Borrador>(() => borradorDe(general ?? { activa: true, parametros: {}, niveles: [{ desde: 0, nivel: 'ADVERTENCIA' }] }));
  const [error, setError] = useState<string | null>(null);
  const [guardando, setGuardando] = useState(false);

  const detalles = useTroncalesConRutas(abierto && (ambito === 'RUTA' || ambito === 'PARADA_RUTA'));
  const troncal = detalles.find((t) => String(t.id) === troncalSel);
  const ruta = troncal?.rutas.find((r) => String(r.id) === rutaSel);
  const opciones = ambito === 'EMPRESA' ? (catalogos?.empresas ?? []) : ambito === 'UNIDAD_NEGOCIO' ? (catalogos?.unidadesNegocio ?? []) : [];
  const refFinal = ambito === 'RUTA' ? rutaSel : ambito === 'PARADA_RUTA' ? ref : ref;
  const existe = refFinal !== '' && existentes.some((r) => r.ambito === ambito && r.ambitoRef === refFinal);

  if (!abierto)
    return (
      <div className="regla-item">
        <div>
          <button type="button" className="btn-sm" onClick={() => setAbierto(true)}>
            <Plus aria-hidden size={13} />
            Regla para una empresa, unidad de negocio, ruta o parada
          </button>
        </div>
      </div>
    );

  const crear = async () => {
    if (!refFinal) return setError('Elegí a qué se aplica la regla.');
    if (existe) return setError('Ya existe una regla de este tipo para ese ámbito: editala en la lista.');
    const e = validar(tipo, b);
    if (e) return setError(e);
    setError(null);
    setGuardando(true);
    try {
      await api.crearReglaAlerta({ tipo, ambito, ambitoRef: refFinal, ...b });
      avisar('Regla creada');
      setAbierto(false);
      setRef('');
      onCreada();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo crear la regla.');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="regla-item">
      <strong>Nueva regla específica</strong>
      <div className="regla-params">
        <label className="field">
          <span>Se aplica a</span>
          <select
            value={ambito}
            onChange={(e) => {
              setAmbito(e.target.value as typeof ambito);
              setRef('');
              setTroncalSel('');
              setRutaSel('');
            }}
          >
            {AMBITOS_REGLA.filter((a) => a !== 'GENERAL').map((a) => (
              <option key={a} value={a}>
                {AMBITO[a]}
              </option>
            ))}
          </select>
        </label>
        {(ambito === 'EMPRESA' || ambito === 'UNIDAD_NEGOCIO') && (
          <label className="field">
            <span>{ambito === 'EMPRESA' ? 'Empresa' : 'Unidad de negocio'}</span>
            <select value={ref} onChange={(e) => setRef(e.target.value)}>
              <option value="">Elegí una opción</option>
              {opciones.map((o) => (
                <option key={o} value={o}>
                  {o}
                </option>
              ))}
            </select>
          </label>
        )}
        {(ambito === 'RUTA' || ambito === 'PARADA_RUTA') && (
          <>
            <label className="field">
              <span>Troncal</span>
              <select
                value={troncalSel}
                onChange={(e) => {
                  setTroncalSel(e.target.value);
                  setRutaSel('');
                  setRef('');
                }}
              >
                <option value="">Elegí una troncal</option>
                {detalles
                  .filter((t) => t.rutas.length > 0)
                  .map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.nombre}
                    </option>
                  ))}
              </select>
            </label>
            <label className="field">
              <span>Ruta</span>
              <select
                value={rutaSel}
                disabled={!troncal}
                onChange={(e) => {
                  setRutaSel(e.target.value);
                  setRef('');
                }}
              >
                <option value="">Elegí una ruta</option>
                {troncal?.rutas.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.nombre}
                  </option>
                ))}
              </select>
            </label>
            {ambito === 'PARADA_RUTA' && (
              <label className="field">
                <span>Parada</span>
                <select value={ref} disabled={!ruta} onChange={(e) => setRef(e.target.value)}>
                  <option value="">Elegí una parada</option>
                  {ruta?.paradas.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.orden}. {p.nombre}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </>
        )}
      </div>
      <EditorBorrador tipo={tipo} b={b} setB={setB} deshabilitado={false} />
      {error && (
        <div className="banner error" role="alert" style={{ marginBottom: 0 }}>
          <TriangleAlert aria-hidden />
          {error}
        </div>
      )}
      <div className="editor-acciones">
        <button type="button" className="primary btn-sm" disabled={guardando} onClick={() => void crear()}>
          <Save aria-hidden size={13} />
          Crear regla
        </button>
        <button type="button" className="btn-sm" onClick={() => setAbierto(false)}>
          Cancelar
        </button>
      </div>
    </div>
  );
}

/** Configuración de alertas (solo ADMIN): reglas por tipo, con umbrales, niveles y ámbito. */
export function ConfiguracionAlertasPagina() {
  const { usuario } = useSesion();
  const permitido = puedeVerReglas(usuario?.rol);
  const editable = puedeEditarReglas(usuario?.rol);
  const [tipo, setTipo] = useState<'' | TipoAlerta>('');
  const [ambito, setAmbito] = useState<'' | AmbitoRegla>('');
  const { datos, cargando, error, recargar } = useCarga(permitido ? () => api.reglasAlerta() : null, [permitido]);
  // Las reglas que se ven en pantalla: de ahí salen los ids de ruta y parada a nombrar.
  const visiblesTodas = useMemo(() => (datos ?? []).filter((r) => (!tipo || r.tipo === tipo) && (!ambito || r.ambito === ambito)), [datos, tipo, ambito]);
  const nombres = useNombresRuteo(visiblesTodas);

  if (!permitido)
    return (
      <div className="page">
        <div className="vacio">
          <ShieldAlert aria-hidden />
          La configuración de alertas es solo para administración.
        </div>
      </div>
    );

  const reglas = datos ?? [];
  const tipos = TIPOS_ALERTA.filter((t) => !tipo || t === tipo);

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <div className="eyebrow">ADMINISTRACIÓN / ALERTAS</div>
          <h1>Configuración de alertas</h1>
        </div>
      </div>

      <div className="banner">
        <Settings2 aria-hidden />
        <span>
          Cada alerta se evalúa con la regla más específica que aplica: parada de ruta, ruta, unidad de negocio, empresa y, por último, la general. Una regla inactiva no genera alertas.
        </span>
      </div>

      <div className="toolbar">
        <label className="field">
          <span>Tipo de alerta</span>
          <select value={tipo} onChange={(e) => setTipo(e.target.value as '' | TipoAlerta)}>
            <option value="">Todos</option>
            {TIPOS_ALERTA.map((t) => (
              <option key={t} value={t}>
                {TITULO_POR_TIPO[t]}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Ámbito</span>
          <select value={ambito} onChange={(e) => setAmbito(e.target.value as '' | AmbitoRegla)}>
            <option value="">Todos</option>
            {AMBITOS_REGLA.map((a) => (
              <option key={a} value={a}>
                {AMBITO[a]}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error && (
        <div className="banner error" role="alert">
          <TriangleAlert aria-hidden />
          {error}
          <button type="button" className="btn-sm" style={{ marginLeft: 'auto' }} onClick={recargar}>
            Reintentar
          </button>
        </div>
      )}
      {cargando && !datos && (
        <div className="skeleton">
          <Loader className="spin" aria-hidden size={16} />
          Cargando reglas…
        </div>
      )}

      <div className="reglas-grid">
        {datos &&
          tipos.map((t) => {
            const Icono = ICONO_TIPO_ALERTA[t];
            const deTipo = reglas.filter((r) => r.tipo === t);
            const visibles = deTipo.filter((r) => !ambito || r.ambito === ambito).sort((a, b) => AMBITOS_REGLA.indexOf(a.ambito) - AMBITOS_REGLA.indexOf(b.ambito));
            return (
              <section key={t} className="card" style={{ marginBottom: 0 }} aria-label={TITULO_POR_TIPO[t]}>
                <header>
                  <h2>
                    <Icono aria-hidden />
                    {TITULO_POR_TIPO[t]}
                  </h2>
                  <span className="muted" style={{ fontSize: 12 }}>
                    {deTipo.length} regla{deTipo.length === 1 ? '' : 's'}
                  </span>
                </header>
                {visibles.map((r) => (
                  <FilaRegla key={`${r.id}-${r.actualizadoEn}`} regla={r} nombres={nombres} puedeEditar={editable} onCambio={recargar} />
                ))}
                {visibles.length === 0 && <div className="vacio">No hay reglas de este tipo con ese ámbito.</div>}
                {editable && !ambito && <NuevaRegla tipo={t} general={deTipo.find((r) => r.ambito === 'GENERAL')} existentes={deTipo} onCreada={recargar} />}
              </section>
            );
          })}
      </div>
    </div>
  );
}
